import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

/**
 * A short, anchored walkthrough of a screen — the mechanism behind the Network
 * Topology first-visit tour, extracted so more than one caller can drive it
 * (CTX-510).
 *
 * Deliberately library-free, and everything here is the original's behaviour
 * rather than a redesign of it:
 *
 * * The spotlight is a positioned div whose 9999px box-shadow dims everything
 *   around the anchor — no SVG mask, no portal.
 * * A step anchors by CSS selector, and a step whose anchor is not currently
 *   mounted **falls back to a centered card** instead of breaking. That is what
 *   lets a tour run against a screen in any data state, and it is also why a
 *   selector cannot be validated when the entry is authored: the DOM it names
 *   does not exist yet.
 * * One deferred re-measure at 350ms catches anchors inside canvases that lay
 *   themselves out asynchronously.
 * * Finishing or skipping writes `storageKey`, and a tour whose key is set does
 *   not mount again for that browser profile — unless the caller says the tour
 *   was asked for rather than offered (see `trigger`).
 *
 * What the caller owns is the content and the identity: the steps, the storage
 * key, and the strings a screen reader and a test reach it by.
 */

export interface TourStep {
	id: string;
	title: string;
	body: string;
	/** CSS selector of the element to spotlight; omit for a centered card. */
	selector?: string;
}

interface GuidedTourProps {
	steps: TourStep[];
	/**
	 * localStorage key that records this tour as done. Distinct per tour —
	 * two tours sharing a key would silently suppress each other.
	 */
	storageKey: string;
	/** Root `data-testid`, so each caller's tour is addressable on its own. */
	testId: string;
	/** Accessible name of the dialog, e.g. `Network Topology tour`. */
	ariaLabel: string;
	/** Small caps label above the step title; omitted renders no badge. */
	eyebrow?: string;
	/**
	 * What put this tour on screen, which is what decides whether `storageKey`
	 * may suppress it (CTX-510 §2).
	 *
	 * `first-visit` is the original behaviour and the default: the tour offers
	 * itself, so a key already set means "this person has seen it" and it stays
	 * down. `explicit` is someone having *asked* for the tour — from the What's
	 * new CTA — and a request is not a first visit, so the key is written on
	 * finish but never read on mount. Without this, the second click of "Walk
	 * me through it" would silently do nothing.
	 */
	trigger?: "first-visit" | "explicit";
	/**
	 * Called once the tour is finished or skipped. The one-shot key already
	 * keeps a `first-visit` tour down, so this exists for the caller that holds
	 * the tour in state — the walkthrough provider clears its pending launch
	 * here, so walking away from the destination and back does not re-run it.
	 */
	onDismiss?: () => void;
}

/**
 * The step's primary action — `Next`, or `Get started` on the last step.
 *
 * The dark half is a token *pair*, not a background with a hardcoded
 * foreground. `bg-success-bold` flips direction between themes — green-700 in
 * light, green-50 in dark — so `text-white` reads as white-on-pale the moment
 * the theme changes. `text-inverse-success` is the foreground built to sit on
 * it and flips the other way (green-50 → green-800), which is what keeps the
 * label legible in both.
 *
 * The hover follows the same logic: `hover:bg-green-700` would darken a
 * background that is already pale and now carries dark text, so in dark the
 * background holds and the affordance comes from opacity — the pattern
 * `GenericButton` uses for its own bold variants.
 */
const PRIMARY_STEP_BUTTON_CLASS = `
	rounded-md px-3 py-1.5 text-xs font-semibold
	bg-green-600 text-white hover:bg-green-700
	dark:bg-semantic-bg-success-bold
	dark:text-semantic-text-inverse-success
	dark:hover:bg-semantic-bg-success-bold dark:hover:opacity-90
`;

/**
 * Which tours are on screen right now, and who wants to know.
 *
 * One dialog must not open over another, and the What's new auto-open is the
 * first thing that could (CTX-500 §2.1 — no auto-open while a tour is running).
 * The register lives *here*, in the component every tour is, rather than in
 * `WalkthroughTourProvider`: that provider owns only the walkthrough launches,
 * and `NetworkTopologyTour` mounts its own `GuidedTour` on Insight Topology
 * outside it. A reader asking the provider would get "no tour" while a tour was
 * spotlighting half the screen.
 *
 * A module-level register rather than a context for the same reason — the two
 * mounts share no ancestor below `AppLayout`, and a provider wrapping
 * everything to carry a boolean is more wiring than the fact deserves.
 */
const activeTours = new Set<symbol>();
const tourListeners = new Set<() => void>();

function setTourActive(id: symbol, isActive: boolean): void {
	if (isActive) activeTours.add(id);
	else activeTours.delete(id);
	tourListeners.forEach((listener) => listener());
}

function subscribeToTours(listener: () => void): () => void {
	tourListeners.add(listener);
	return () => {
		tourListeners.delete(listener);
	};
}

/**
 * Whether any `GuidedTour` is currently on screen.
 *
 * The server snapshot is `false`: nothing is spotlighted in markup that has not
 * hydrated, and reporting a tour there would suppress a dialog for no reason.
 */
export function useIsGuidedTourRunning(): boolean {
	return useSyncExternalStore(
		subscribeToTours,
		() => activeTours.size > 0,
		() => false
	);
}

interface SpotlightRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

function rectFor(selector?: string): SpotlightRect | null {
	if (!selector) return null;
	const el = document.querySelector(selector);
	if (!el) return null;
	const r = el.getBoundingClientRect();
	if (r.width === 0 || r.height === 0) return null;
	const pad = 8;
	return { top: r.top - pad, left: r.left - pad, width: r.width + 2 * pad, height: r.height + 2 * pad };
}

export const GuidedTour: React.FC<GuidedTourProps> = ({
	steps,
	storageKey,
	testId,
	ariaLabel,
	eyebrow,
	trigger = "first-visit",
	onDismiss,
}) => {
	const [dismissed, setDismissed] = useState<boolean>(() => {
		// An explicit launch skips the check entirely rather than clearing the
		// key: the key still records that the tour was completed, and a tour
		// somebody asked for should not depend on whether they asked before.
		if (trigger === "explicit") return false;
		try {
			return typeof window === "undefined" || window.localStorage.getItem(storageKey) != null;
		} catch {
			return true; // storage unavailable — never nag on every visit
		}
	});
	const [stepIndex, setStepIndex] = useState(0);
	const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);

	const step = steps[stepIndex];
	const isOnScreen = !dismissed && Boolean(step);

	/**
	 * Register while actually rendering — the same condition as the early
	 * return below, so the answer can never claim a tour the reader cannot see
	 * (a suppressed `first-visit` tour, or an empty `steps`).
	 */
	useEffect(() => {
		if (!isOnScreen) return undefined;
		const id = Symbol("guided-tour");
		setTourActive(id, true);
		return () => setTourActive(id, false);
	}, [isOnScreen]);

	const finish = useCallback(() => {
		try {
			window.localStorage.setItem(storageKey, new Date().toISOString());
		} catch {
			// best effort — dismissing still hides it for this session
		}
		setDismissed(true);
		onDismiss?.();
	}, [storageKey, onDismiss]);

	// Re-measure the anchored element on step change and on resize; anchors
	// live inside a canvas that lays itself out asynchronously, so one
	// deferred re-measure catches late mounts.
	useEffect(() => {
		if (dismissed) return;
		const measure = () => setSpotlight(rectFor(step?.selector));
		measure();
		const timer = window.setTimeout(measure, 350);
		window.addEventListener("resize", measure);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("resize", measure);
		};
	}, [dismissed, step]);

	const cardPosition = useMemo(() => {
		if (!spotlight) return null;
		const below = spotlight.top + spotlight.height + 12;
		const fitsBelow = below + 220 < window.innerHeight;
		return {
			top: fitsBelow ? below : Math.max(12, spotlight.top - 232),
			left: Math.min(Math.max(12, spotlight.left), Math.max(12, window.innerWidth - 372)),
		};
	}, [spotlight]);

	if (dismissed || !step) return null;

	const isLast = stepIndex === steps.length - 1;

	return (
		<div data-testid={testId} className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={ariaLabel}>
			{spotlight ? (
				<div
					data-testid="tour-spotlight"
					className="absolute rounded-xl border-2 border-green-500 transition-all duration-200"
					style={{
						top: spotlight.top,
						left: spotlight.left,
						width: spotlight.width,
						height: spotlight.height,
						boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
					}}
				/>
			) : (
				<div className="absolute inset-0 bg-slate-900/55" />
			)}

			<div
				data-testid="tour-card"
				className="absolute flex w-[360px] flex-col gap-3 rounded-xl border border-semantic-border-secondary bg-semantic-bg-primary p-4 shadow-xl"
				style={cardPosition ?? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
			>
				<div className="flex items-center justify-between">
					{eyebrow ? (
						<span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700 dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success">
							{eyebrow}
						</span>
					) : (
						/* Keeps the counter hard right when there is no badge —
						   `justify-between` with one child would centre it. */
						<span />
					)}
					<span className="text-[11px] text-semantic-text-secondary">
						{stepIndex + 1} / {steps.length}
					</span>
				</div>
				<div className="text-sm font-bold text-semantic-text-primary">{step.title}</div>
				<div className="text-xs leading-relaxed text-semantic-text-secondary">{step.body}</div>
				<div className="mt-1 flex items-center justify-between">
					<button
						type="button"
						data-testid="tour-skip"
						onClick={finish}
						className="text-xs text-semantic-text-secondary underline-offset-2 hover:underline"
					>
						Skip tour
					</button>
					<div className="flex items-center gap-2">
						{stepIndex > 0 && (
							<button
								type="button"
								data-testid="tour-back"
								onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
								className="rounded-md border border-semantic-border-secondary px-3 py-1.5 text-xs font-semibold text-semantic-text-primary hover:bg-semantic-state-hover"
							>
								Back
							</button>
						)}
						<button
							type="button"
							data-testid="tour-next"
							onClick={() => (isLast ? finish() : setStepIndex((index) => index + 1))}
							className={PRIMARY_STEP_BUTTON_CLASS}
						>
							{isLast ? "Get started" : "Next"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
